import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SectionLink from "@/components/SectionLink";

// One number for the whole site — the footer and the Visit card show the same
// one, so a visitor never has to guess which to ring.
const PHONE_DISPLAY = "(555) 010-2030";
const PHONE_TEL = "+15550102030";

// A `hash` link points at a section of the home page; a `to` link is a page of
// its own. The header is on both pages now, so the same list has to work from
// either one.
type NavLink = { label: string; hash?: string; to?: string };

const NAV_LINKS: NavLink[] = [
  { label: "Home", hash: "#home" },
  { label: "About", hash: "#about" },
  { label: "Coffee", hash: "#coffee" },
  { label: "Wholesale", to: "/wholesale" },
  { label: "Visit", hash: "#visit" },
  { label: "Editorial", hash: "#editorial" },
];

/** One entry, in the bar or in the menu — the two show the same list. */
function NavItem({
  link,
  className,
  onClick,
}: {
  link: NavLink;
  className: string;
  onClick?: () => void;
}) {
  if (link.to) {
    return (
      <Link to={link.to} className={className} onClick={onClick}>
        {link.label}
      </Link>
    );
  }
  if (!link.hash) return null;
  return (
    <SectionLink hash={link.hash} className={className} onClick={onClick}>
      {link.label}
    </SectionLink>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-black/55 backdrop-blur-sm"
      }`}
    >
      <nav className="w-full px-6 md:px-10 lg:px-14 py-4 flex items-center justify-between">
        <SectionLink hash="#home" className="flex items-center gap-3 cursor-pointer">
          <img
            src={`${import.meta.env.BASE_URL}logo.png`}
            alt="Meridian Coffee Roasters"
            className={`h-10 w-10 object-contain transition-all ${
              scrolled ? "" : "brightness-0 invert"
            }`}
          />
          <span className="flex flex-col leading-tight">
            <span
              className={`font-heading text-xl tracking-tight transition-colors ${
                scrolled ? "text-primary-900" : "text-background-50"
              }`}
            >
              Meridian
            </span>
            {/* Quiet second line, not a heading — and gone on the narrowest
                phones, where the bar has no room for it. */}
            <span
              className={`hidden min-[380px]:block text-[0.7rem] tracking-wide transition-colors ${
                scrolled ? "text-foreground-600" : "text-background-50/75"
              }`}
            >
              Roasted fresh, delivered weekly.
            </span>
          </span>
        </SectionLink>

        {/* Six links now, plus the phone number and the button. That row needs
            more width than a 1024px screen has, so it waits for xl; below that
            it is the menu, which carries every one of them. */}
        <ul className="hidden xl:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <li key={link.label}>
              <NavItem
                link={link}
                className={`text-sm tracking-wide uppercase transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-800 hover:text-accent-700"
                    : "text-background-50/90 hover:text-accent-300"
                }`}
              />
            </li>
          ))}
        </ul>

        <div className="hidden xl:flex items-center gap-6">
          <a
            href={`tel:${PHONE_TEL}`}
            className={`inline-flex items-center gap-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? "text-foreground-800 hover:text-accent-700"
                : "text-background-50/90 hover:text-accent-300"
            }`}
          >
            <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
            <span>{PHONE_DISPLAY}</span>
          </a>

          <SectionLink
            hash="#coffee"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? "bg-primary-900 text-background-50 hover:bg-primary-800"
                : "bg-background-50 text-primary-900 hover:bg-accent-100"
            }`}
          >
            <span>Shop Beans</span>
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </SectionLink>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          className={`xl:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
            scrolled ? "text-primary-900" : "text-background-50"
          }`}
        >
          <i
            className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl w-6 h-6 flex items-center justify-center`}
          ></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="xl:hidden bg-background-50 border-t border-background-200 px-6 py-6">
          <ul className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <NavItem
                  link={link}
                  className="block text-base text-foreground-800 hover:text-accent-700 cursor-pointer whitespace-nowrap"
                  onClick={() => setMobileOpen(false)}
                />
              </li>
            ))}
            <li>
              <a
                href={`tel:${PHONE_TEL}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-base text-foreground-800 hover:text-accent-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <SectionLink
                hash="#coffee"
                onClick={() => setMobileOpen(false)}
                className="inline-flex mt-2 items-center gap-2 px-5 py-2.5 rounded-full bg-primary-900 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                Shop Beans
              </SectionLink>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}